'use client';
import { PanelResizeHandle } from '@/components/panel-resize-handle';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail
} from '@/components/ui/sidebar';
import {
  navItems,
  NAV_GROUP_ORDER,
  NAV_GROUP_LABELS
} from '@/config/nav-config';
import type { NavGroup } from '@/config/nav-config';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useFilteredNavItems } from '@/hooks/use-nav';
import { usePanelWidth } from '@/hooks/use-panel-width';
import {
  NAV_SIDEBAR_BOUNDS,
  NAV_SIDEBAR_DEFAULT_WIDTH
} from '@/lib/panel-resize';
import { usePermissions } from '@/hooks/use-permissions';
import { useSidebarGroups } from '@/hooks/use-sidebar-groups';
import { IconChevronRight } from '@tabler/icons-react';
import { Link, usePathname } from '@/i18n/routing';
import { useFirstAccessibleRoute } from '@/hooks/use-nav';
import Image from 'next/image';
import * as React from 'react';
import { Icons } from '../icons';
import { useTranslations } from 'next-intl';
import type { NavItem } from '@/types';

/** Nav sidebar sizing. dashboard/layout.tsx needs the same numbers to render
 *  the cookie width server-side, but cannot import them: this module is
 *  'use client', so on the server every export here is a client reference
 *  rather than the value. It duplicates them — keep the two in sync. */

export default function AppSidebar({
  side,
  dir
}: {
  side?: 'left' | 'right';
  dir?: 'ltr' | 'rtl';
}) {
  const pathname = usePathname();
  const { isOpen } = useMediaQuery();
  const itemsToShow = useFilteredNavItems(navItems);
  const t = useTranslations();
  const { hasPermission } = usePermissions();
  const canViewLogo = hasPermission('branding.view_logo');
  const home = useFirstAccessibleRoute();
  const { openGroups, toggleGroup } = useSidebarGroups();
  const { handleProps } = usePanelWidth('nav-sidebar', {
    defaultWidth: NAV_SIDEBAR_DEFAULT_WIDTH,
    min: NAV_SIDEBAR_BOUNDS.min,
    max: NAV_SIDEBAR_BOUNDS.max,
    // The nav sidebar is the one panel at the page's inline START, so its
    // handle is on its inline-END edge — right in LTR, left in RTL.
    edge: 'end',
    onCommit: (next) => {
      // Mirrored for the server render only; see dashboard/layout.tsx.
      document.cookie = `sidebar_width=${next}; path=/; max-age=${60 * 60 * 24 * 7}`;
    }
  });

  // Hydrate persisted store on mount
  React.useEffect(() => {
    useSidebarGroups.persist.rehydrate();
  }, []);

  React.useEffect(() => {
    // Side effects based on sidebar state changes
  }, [isOpen]);

  const isPathActive = (url: string) => {
    if (pathname === url) return true;
    const depth = url.split('/').filter(Boolean).length;
    return depth >= 2 && pathname.startsWith(url + '/');
  };

  const resolveTitle = (item: NavItem) => {
    if (item.titleKey) {
      try {
        return t(item.titleKey as never);
      } catch {
        return item.title;
      }
    }
    return item.title;
  };

  // Group items by their group field
  const groupedItems = React.useMemo(() => {
    const groups = new Map<string, NavItem[]>();
    for (const item of itemsToShow) {
      const group = item.group ?? 'administration';
      const existing = groups.get(group);
      if (existing) {
        existing.push(item);
      } else {
        groups.set(group, [item]);
      }
    }
    return groups;
  }, [itemsToShow]);

  const renderNavItem = (item: NavItem) => {
    const Icon = item.icon ? Icons[item.icon] : Icons.logo;
    const title = resolveTitle(item);

    if (item?.items && item?.items?.length > 0) {
      return (
        <Collapsible
          key={item.title}
          asChild
          defaultOpen={
            item.items?.some((s) => isPathActive(s.url)) ??
            isPathActive(item.url)
          }
          className='group/collapsible'
        >
          <SidebarMenuItem>
            <CollapsibleTrigger asChild>
              <SidebarMenuButton
                tooltip={title}
                isActive={
                  item.items?.some((s) => isPathActive(s.url)) ??
                  isPathActive(item.url)
                }
              >
                {item.icon && <Icon />}
                <span>{title}</span>
                <IconChevronRight className='ms-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 rtl:rotate-180 rtl:group-data-[state=open]/collapsible:rotate-90' />
              </SidebarMenuButton>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarMenuSub>
                {item.items?.map((subItem) => (
                  <SidebarMenuSubItem key={subItem.title}>
                    <SidebarMenuSubButton
                      asChild
                      isActive={isPathActive(subItem.url)}
                    >
                      <Link href={subItem.url} prefetch={false}>
                        <span>{resolveTitle(subItem)}</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            </CollapsibleContent>
          </SidebarMenuItem>
        </Collapsible>
      );
    }

    return (
      <SidebarMenuItem key={item.title}>
        <SidebarMenuButton
          asChild
          tooltip={title}
          isActive={isPathActive(item.url)}
        >
          <Link href={item.url} prefetch={false}>
            <Icon />
            <span>{title}</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar collapsible='icon' side={side} dir={dir}>
      <SidebarHeader className='p-2'>
        {canViewLogo && (
          <Link
            href={home}
            className='flex w-6/12 items-center gap-2 px-1 py-1'
          >
            <Image
              src='/logo/logo.png'
              alt='Safha Archive'
              width={160}
              height={80}
              priority
              style={{ width: 'auto', height: 'auto' }}
              className='block shrink-0 rounded-md md:hidden dark:hidden'
            />
            <Image
              src='/logo/dark-logo.png'
              alt='Safha Archive'
              width={160}
              height={28}
              priority
              style={{ width: 'auto', height: 'auto' }}
              className='hidden shrink-0 rounded-md dark:block md:dark:hidden'
            />
          </Link>
        )}
      </SidebarHeader>
      <SidebarContent className='[scrollbar-width:none] overflow-x-hidden [-ms-overflow-style:none] group-data-[collapsible=icon]:overflow-y-auto [&::-webkit-scrollbar]:hidden'>
        {NAV_GROUP_ORDER.map((groupKey) => {
          const items = groupedItems.get(groupKey);
          if (!items || items.length === 0) return null;
          const isOpen = openGroups[groupKey] ?? true;

          return (
            <Collapsible
              key={groupKey}
              open={isOpen}
              onOpenChange={() => toggleGroup(groupKey)}
              className='group/collapsible'
            >
              <SidebarGroup>
                <SidebarGroupLabel asChild>
                  <CollapsibleTrigger className='flex w-full items-center'>
                    {t(NAV_GROUP_LABELS[groupKey as NavGroup] as never)}
                    <IconChevronRight className='ms-auto size-4 transition-transform duration-200 ease-in-out group-data-[state=open]/collapsible:rotate-90 rtl:rotate-180 rtl:group-data-[state=open]/collapsible:rotate-90' />
                  </CollapsibleTrigger>
                </SidebarGroupLabel>
                <CollapsibleContent className='data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down overflow-hidden transition-all duration-200 ease-in-out'>
                  <SidebarMenu>{items.map(renderNavItem)}</SidebarMenu>
                </CollapsibleContent>
              </SidebarGroup>
            </Collapsible>
          );
        })}
      </SidebarContent>
      <SidebarFooter />
      <SidebarRail
        aria-label={t('common.toggleSidebar')}
        title={t('common.toggleSidebar')}
      />
      {/* Resize lives beside the rail, not on it: the rail owns
          click-to-toggle, and a drag that also toggled would be unusable.
          Hidden while collapsed — there is no width to drag. */}
      <PanelResizeHandle
        className='absolute inset-y-0 end-0 group-data-[state=collapsed]:hidden'
        {...handleProps}
      />
    </Sidebar>
  );
}
